import { getConfig } from '@/api/config'

/**
 * adapted from google's recommended dynamic-library bootstrap loader.
 * installs window.google.maps.importLibrary as a thunk that lazy-loads
 * the maps script the first time any library is requested, then forwards
 * to the real importLibrary once it's available. idempotent so multiple
 * callers safely share the same script tag.
 */
function installBootstrapLoader(apiKey: string): void {
  const callbackName = '__ib__'
  const params = { key: apiKey, v: 'weekly' } as Record<string, string>
  const requested = new Set<string>()
  let scriptPromise: Promise<void> | null = null

  type GoogleRoot = Record<string, unknown>
  const win = window as unknown as GoogleRoot
  const google = (win.google as GoogleRoot) ?? ((win.google = {}) as GoogleRoot)
  const maps = (google.maps as GoogleRoot) ?? ((google.maps = {}) as GoogleRoot)

  function loadScript(): Promise<void> {
    if (scriptPromise) return scriptPromise
    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      const search = new URLSearchParams()
      search.set('libraries', [...requested].join(','))
      for (const paramKey in params) {
        const snake = paramKey.replace(
          /[A-Z]/g,
          letter => '_' + letter.toLowerCase()
        )
        search.set(snake, params[paramKey]!)
      }
      search.set('callback', 'google.maps.' + callbackName)
      script.src = `https://maps.googleapis.com/maps/api/js?${search}`
      maps[callbackName] = resolve
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('The Google Maps JavaScript API could not load.'))
      }
      script.nonce =
        document.querySelector('script[nonce]')?.getAttribute('nonce') ?? ''
      document.head.append(script)
    })
    return scriptPromise
  }

  if (!maps.importLibrary) {
    maps.importLibrary = (library: string, ...rest: unknown[]) => {
      requested.add(library)
      return loadScript().then(() =>
        (maps.importLibrary as (...args: unknown[]) => unknown)(
          library,
          ...rest
        )
      )
    }
  }
}

let bootstrapInstalled = false
const libraryPromises = new Map<string, Promise<boolean>>()

/** where google is blocked (mainland china) the script request tends to hang
 *  rather than error, which would leave callers on "loading" forever. */
export const GOOGLE_LOAD_TIMEOUT_MS = 10_000

/**
 * loads a google maps js sdk library on demand. shares the bootstrap
 * loader across consumers so the api key is only fetched once, and
 * caches per library so repeated calls return the same promise. resolves
 * false when no api key is configured, the script fails, or it takes longer
 * than GOOGLE_LOAD_TIMEOUT_MS (caller renders a fallback). a failure is not
 * cached, so the next mount retries.
 */
export function loadGoogleLibrary(name: string): Promise<boolean> {
  const existing = libraryPromises.get(name)
  if (existing) return existing

  const load = (async () => {
    const config = await getConfig()
    const apiKey = config.googlePlacesApiKey
    if (!apiKey) return false
    if (!bootstrapInstalled) {
      installBootstrapLoader(apiKey)
      bootstrapInstalled = true
    }
    await google.maps.importLibrary(name)
    return true
  })()

  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<boolean>(resolve => {
    timer = setTimeout(() => resolve(false), GOOGLE_LOAD_TIMEOUT_MS)
  })

  const promise = Promise.race([load.catch(() => false), timeout]).then(
    loaded => {
      clearTimeout(timer)
      if (!loaded) libraryPromises.delete(name)
      return loaded
    }
  )

  libraryPromises.set(name, promise)
  return promise
}
